import axios from 'axios';
import { msalConfig } from '../authConfig';
import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { getAccessToken } from './TokenService';

const backendBaseUrl = msalConfig.baseUrl;

export interface Conversation {
  id: number;
  userName?: string;
  email: string;
  companyName: string;
  createdAt: string;
}

export interface Message {
  id: number;
  conversationId: number;
  role: string;
  content: string;
  citationsJson?: string;
  executionMode?: string;
  knowledgeSource?: string;
  promptSettingsId?: number;
  memoPromptName?: string;
  createdAt: string;
}

export interface ConversationWithMessages extends Conversation {
  messages: Message[];
}

// Fetch user's conversations
export async function getConversations(
  msalInstance: IPublicClientApplication,
  account: AccountInfo
): Promise<Conversation[]> {
  const token = await getAccessToken(msalInstance, account);
  
  const response = await axios.get(`${backendBaseUrl}api/conversations`, {
    headers: {
      Authorization: `Bearer ${token}`,
    }
  });

  return response.data;
}

// Fetch messages for a specific conversation
export async function getConversationMessages(
  msalInstance: IPublicClientApplication,
  account: AccountInfo,
  conversationId: number
): Promise<Message[]> {
  const token = await getAccessToken(msalInstance, account);
  
  const response = await axios.get(`${backendBaseUrl}api/conversations/${conversationId}/messages`, {
    headers: {
      Authorization: `Bearer ${token}`,
    }
  });

  return response.data;
}

// Fetch a complete conversation with its messages
export async function getConversationWithMessages(
  msalInstance: IPublicClientApplication,
  account: AccountInfo,
  conversationId: number
): Promise<ConversationWithMessages> {
  const token = await getAccessToken(msalInstance, account);
  
  // Fetch conversation details and messages in parallel
  const [conversationsResponse, messagesResponse] = await Promise.all([
    axios.get(`${backendBaseUrl}api/conversations`, {
      headers: { Authorization: `Bearer ${token}` }
    }),
    axios.get(`${backendBaseUrl}api/conversations/${conversationId}/messages`, {
      headers: { Authorization: `Bearer ${token}` }
    })
  ]);

  const conversations = conversationsResponse.data as Conversation[];
  const conversation = conversations.find(c => c.id === conversationId);
  
  if (!conversation) {
    throw new Error(`Conversation with ID ${conversationId} not found`);
  }

  return {
    ...conversation,
    messages: messagesResponse.data
  };
}
